import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useState, useCallback } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ActivityIndicator,
  Animated,
  ScrollView,
  ImageBackground,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import kingdomScene from '../../../assets/iqes/sunset-cross.png';
import { DSText } from '../../design-system/components/DSText';
import { useAppTheme } from '../../design-system/ThemeProvider';
import { spacing, radius } from '../../design-system/tokens';
import { t } from '../../i18n/translations';
import { fetchQuestions } from '../../shared/services/supabaseService';
import { iqesColors, iqesGlow } from '../../shared/ui/iqesDesignSystem';
import { useLanguageStore } from '../quiz-core/languageStore';

import OnboardingLanguageSelector from './components/OnboardingLanguageSelector';

interface OnboardingFlowProps {
  onComplete: () => void;
}

interface QuizQuestion {
  id: number | string;
  question: string;
  options: string[];
  answer: string;
  correct_answer: string;
  explanation: string;
}

interface RawOnboardingQuestion {
  id: number | string;
  question?: unknown;
  options?: unknown;
  answer?: unknown;
  correct_answer?: unknown;
  explanation?: unknown;
  format_data?: {
    explanation?: unknown;
  };
}

interface OnboardingThemeColors {
  accent: string;
  correct: string;
  gradientCorrect: readonly string[];
  gradientPrimary: readonly string[];
  incorrect: string;
  onSurface: string;
  onSurfaceVariant: string;
  primary: string;
  surfaceContainerHigh: string;
  white: string;
}

type Phase = 'welcome' | 'quiz' | 'celebration';

const QUIZ_QUESTION_COUNT = 3;

// Hardcoded fallback questions so onboarding never fails, even offline.
const FALLBACK_QUESTIONS: QuizQuestion[] = [
  {
    id: 'onboard-1',
    question: 'In which town was Jesus born?',
    options: ['Nazareth', 'Bethlehem', 'Jerusalem', 'Capernaum'],
    answer: 'Bethlehem',
    correct_answer: 'Bethlehem',
    explanation: 'Jesus was born in Bethlehem of Judea, as prophesied in Micah 5:2.',
  },
  {
    id: 'onboard-2',
    question: 'How many disciples did Jesus choose?',
    options: ['7', '10', '12', '15'],
    answer: '12',
    correct_answer: '12',
    explanation: 'Jesus chose 12 disciples, also called apostles, to follow Him and spread His teachings.',
  },
  {
    id: 'onboard-3',
    question: 'What was the first miracle Jesus performed?',
    options: ['Healing a blind man', 'Walking on water', 'Turning water into wine', 'Feeding 5,000 people'],
    answer: 'Turning water into wine',
    correct_answer: 'Turning water into wine',
    explanation: 'At a wedding in Cana, Jesus turned water into wine — His first recorded miracle (John 2:1-11).',
  },
];
// Life of Jesus (ID 4) - engaging, accessible content for new users
const ONBOARDING_CATEGORY_ID = 4;
// Easy difficulty
const ONBOARDING_DIFFICULTY_ID = 1;

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const isSmallDevice = SCREEN_HEIGHT < 850;

const OnboardingFlow: React.FC<OnboardingFlowProps> = ({ onComplete }) => {
  const { colors } = useAppTheme();
  const styles = createStyles(colors);
  const { language } = useLanguageStore();
  const [phase, setPhase] = useState<Phase>('welcome');

  // Quiz state
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Animation
  const [fadeAnim] = useState(new Animated.Value(1));

  const fadeTransition = useCallback((callback: () => void) => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      callback();
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();
    });
  }, [fadeAnim]);

  const loadQuestions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const fetched = await fetchQuestions({
        categoryId: ONBOARDING_CATEGORY_ID,
        difficultyId: ONBOARDING_DIFFICULTY_ID,
        limit: QUIZ_QUESTION_COUNT,
        languageCode: language,
        useSmartSelection: false,
      });

      // Filter to MCQ Single only and normalize shape
      const mcqQuestions: QuizQuestion[] = fetched
        .filter((q: RawOnboardingQuestion & { question_type?: unknown }) => !q.question_type || q.question_type === 'mcq_single')
        .map((q: RawOnboardingQuestion) => ({
          id: q.id,
          question: typeof q.question === 'string' ? q.question : '',
          options: Array.isArray(q.options)
            ? q.options.map((option) => {
              if (typeof option === 'string') return option;
              if (option && typeof option === 'object' && 'text' in option) {
                const value = (option as { text?: unknown }).text;
                return typeof value === 'string' ? value : String(value ?? '');
              }
              return String(option ?? '');
            })
            : [],
          answer: typeof q.answer === 'string' ? q.answer : typeof q.correct_answer === 'string' ? q.correct_answer : '',
          correct_answer: typeof q.correct_answer === 'string' ? q.correct_answer : typeof q.answer === 'string' ? q.answer : '',
          explanation: typeof q.explanation === 'string'
            ? q.explanation
            : typeof q.format_data?.explanation === 'string'
              ? q.format_data.explanation
              : '',
        }))
        .filter((q) => typeof q.question === 'string' && q.question.trim().length > 0)
        .filter((q) => q.options.filter((option) => typeof option === 'string' && option.trim().length > 0).length >= 2)
        .slice(0, QUIZ_QUESTION_COUNT);

      if (mcqQuestions.length === 0) {
        console.warn('[Onboarding] No questions from DB, using fallback');
        setQuestions(FALLBACK_QUESTIONS);
      } else {
        setQuestions(mcqQuestions);
      }
    } catch {
      console.warn('[Onboarding] Failed to fetch questions, using fallback');
      setQuestions(FALLBACK_QUESTIONS);
    } finally {
      setIsLoading(false);
    }
  }, [language]);

  const handleStartQuiz = () => {
    fadeTransition(() => {
      setPhase('quiz');
      loadQuestions();
    });
  };

  const handleSelectAnswer = (answer: string) => {
    if (showFeedback || selectedAnswer) return;
    setSelectedAnswer(answer);
    setShowFeedback(true);

    const currentQ = questions[currentQuestionIndex];
    const isCorrect =
      answer === currentQ.answer ||
      answer === currentQ.correct_answer;

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }

    // Auto-advance after brief feedback
    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        fadeTransition(() => {
          setCurrentQuestionIndex((prev) => prev + 1);
          setSelectedAnswer(null);
          setShowFeedback(false);
        });
      } else {
        // Quiz complete
        fadeTransition(() => {
          setPhase('celebration');
        });
      }
    }, 1500);
  };

  const handleSkip = () => {
    onComplete();
  };

  const getOptionStyle = (option: string) => {
    if (!showFeedback) {
      if (selectedAnswer === option) {
        return styles.optionSelected;
      }
      return styles.optionDefault;
    }

    const currentQ = questions[currentQuestionIndex];
    const isCorrect = option === currentQ.answer || option === currentQ.correct_answer;
    const isSelected = selectedAnswer === option;

    if (isCorrect) return styles.optionCorrect;
    if (isSelected && !isCorrect) return styles.optionWrong;
    return styles.optionDefault;
  };

  const getOptionTextColor = (option: string): string => {
    if (!showFeedback) {
      return selectedAnswer === option ? iqesColors.goldBright : iqesColors.ivory;
    }

    const currentQ = questions[currentQuestionIndex];
    const isCorrect = option === currentQ.answer || option === currentQ.correct_answer;
    const isSelected = selectedAnswer === option;

    if (isCorrect) return '#C9FF9B';
    if (isSelected && !isCorrect) return '#FFB4A8';
    return 'rgba(255, 248, 232, 0.58)';
  };

  const getOptionTextWeight = (option: string): '400' | '600' => {
    if (!showFeedback) {
      return selectedAnswer === option ? '600' : '400';
    }

    const currentQ = questions[currentQuestionIndex];
    const isCorrect = option === currentQ.answer || option === currentQ.correct_answer;
    const isSelected = selectedAnswer === option;

    if (isCorrect || isSelected) return '600';
    return '400';
  };

  const renderWelcome = () => (
    <View style={styles.phaseContainer}>
      <View style={styles.welcomeContent}>
        <View style={styles.welcomeCard}>
          <View style={styles.welcomeMark}>
            <DSText variant="title" color={iqesColors.goldBright} style={styles.welcomeMarkText}>
              KBQ
            </DSText>
            <DSText variant="caption" color={iqesColors.goldSoft} uppercase style={styles.welcomeMarkSubtext}>
              Kingdom
            </DSText>
          </View>
          <DSText variant="label" color={iqesColors.goldSoft} uppercase style={styles.eyebrow}>
            {t('onboarding.welcomeEyebrow', language) || 'Scripture Adventure'}
          </DSText>

          <DSText
            variant="display"
            color={iqesColors.ivory}
            align="center"
            numberOfLines={3}
            adjustsFontSizeToFit
            minimumFontScale={0.7}
            style={styles.welcomeTitle}
          >
            {t('onboarding.welcomeTitle', language) || 'Welcome to\nKingdom Bible Quiz'}
          </DSText>
          <DSText
            variant="body"
            color={iqesColors.goldSoft}
            align="center"
            style={styles.welcomeSubtitle}
          >
            {t('onboarding.welcomeSubtitle', language) || 'A daily return to God\'s Word'}
          </DSText>
          <DSText
            variant="body"
            color={iqesColors.mutedWarm}
            align="center"
            style={styles.welcomeDescription}
          >
            {t('onboarding.welcomeDescription', language) ||
              'Let\'s discover how much you already know about the Bible. Ready for a quick quiz?'}
          </DSText>

          <View style={styles.journeyStats}>
            <View style={styles.statPill}>
              <DSText variant="label" color={iqesColors.goldBright} uppercase style={styles.statLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>
                30K+
              </DSText>
              <DSText variant="caption" color={iqesColors.mutedWarm} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{t('onboarding.statQuestions', language) || 'Questions'}</DSText>
            </View>
            <View style={styles.statPill}>
              <DSText variant="label" color={iqesColors.goldBright} uppercase style={styles.statLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>
                10
              </DSText>
              <DSText variant="caption" color={iqesColors.mutedWarm} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{t('onboarding.statLanguages', language) || 'Languages'}</DSText>
            </View>
            <View style={styles.statPill}>
              <DSText variant="label" color={iqesColors.goldBright} uppercase style={styles.statLabel} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>
                {t('onboarding.statDaily', language) || 'Daily'}
              </DSText>
              <DSText variant="caption" color={iqesColors.mutedWarm} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.6}>{t('onboarding.statJourney', language) || 'Journey'}</DSText>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.welcomeActions}>
        <TouchableOpacity testID="onboarding-welcome-cta" style={styles.primaryButton} onPress={handleStartQuiz} activeOpacity={0.8}>
          <LinearGradient
            colors={[...colors.gradientPrimary].reverse() as [string, string]}
            style={styles.primaryButtonGradient}
          >
            <Ionicons name="sparkles" size={20} color={colors.white} style={{ marginRight: spacing.sm }} />
            <DSText variant="title" color={colors.white}>
              {t('onboarding.welcomeCTA', language) || 'Let\'s Find Out'}
            </DSText>
          </LinearGradient>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderQuiz = () => {
    if (isLoading) {
      return (
        <View style={styles.phaseContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
            <DSText variant="body" color={colors.onSurfaceVariant} align="center">
              {t('onboarding.loading', language) || 'Preparing your quiz...'}
            </DSText>
          </View>
        </View>
      );
    }

    if (loadError || questions.length === 0) {
      return (
        <View style={styles.phaseContainer}>
          <View style={styles.loadingContainer}>
            <Ionicons name="cloud-offline" size={48} color={colors.onSurfaceVariant} />
            <DSText variant="body" color={colors.onSurfaceVariant} align="center">
              {t('onboarding.errorLoading', language) || 'Couldn\'t load questions. Let\'s skip ahead!'}
            </DSText>
            <TouchableOpacity style={styles.skipAheadButton} onPress={onComplete}>
              <DSText variant="body" color={colors.primary} style={{ fontWeight: '600' }}>
                {t('onboarding.celebrationCTA', language) || 'Start Exploring'}
              </DSText>
            </TouchableOpacity>
          </View>
        </View>
      );
    }

    const currentQ = questions[currentQuestionIndex];

    return (
      <View style={styles.phaseContainer}>
        {/* Progress bar */}
        <View style={styles.quizProgressContainer}>
          <DSText
            variant="label"
            color={iqesColors.goldSoft}
            uppercase
            style={styles.quizHeaderText}
          >
            {t('onboarding.quizHeader', language) || 'Your First Quiz'}
          </DSText>
          <View style={styles.progressBar}>
            {questions.map((_, idx) => (
              <View
                key={idx}
                style={[
                  styles.progressDot,
                  idx < currentQuestionIndex && styles.progressDotCompleted,
                  idx === currentQuestionIndex && styles.progressDotActive,
                ]}
              />
            ))}
          </View>
          <DSText variant="caption" color={iqesColors.mutedWarm} style={styles.quizQuestionCount}>
            {(t('onboarding.quizQuestion', language) || 'Question {current} of {total}')
              .replace('{current}', String(currentQuestionIndex + 1))
              .replace('{total}', String(questions.length))}
          </DSText>
        </View>

        <ScrollView
          style={styles.quizScrollView}
          contentContainerStyle={styles.quizScrollContent}
          showsVerticalScrollIndicator={false}
          bounces={false}
        >
          {/* Question */}
          <Animated.View style={[styles.questionCard, { opacity: fadeAnim }]}>
            <DSText
              variant="title"
              color={iqesColors.ivory}
              align="center"
              style={styles.questionText}
            >
              {currentQ.question}
            </DSText>

            {/* Options */}
            <View style={styles.optionsContainer}>
              {currentQ.options.map((option, idx) => (
                <TouchableOpacity
                  key={idx}
                  testID={`onboarding-option-${idx}`}
                  style={[styles.optionButton, getOptionStyle(option)]}
                  onPress={() => handleSelectAnswer(option)}
                  disabled={showFeedback}
                  activeOpacity={0.7}
                >
                  <View style={styles.optionContent}>
                    <View style={[
                      styles.optionBadge,
                      showFeedback && (option === currentQ.answer || option === currentQ.correct_answer)
                        ? styles.optionBadgeCorrect
                        : showFeedback && selectedAnswer === option
                          ? styles.optionBadgeWrong
                      : null,
                    ]}>
                      <DSText variant="caption" color={iqesColors.canvas} style={styles.optionBadgeText}>
                        {String.fromCharCode(65 + idx)}
                      </DSText>
                    </View>
                    <DSText
                      variant="body"
                      color={getOptionTextColor(option)}
                      style={[styles.optionText, { fontWeight: getOptionTextWeight(option) }]}
                      numberOfLines={3}
                    >
                      {option}
                    </DSText>
                    {showFeedback && (option === currentQ.answer || option === currentQ.correct_answer) && (
                      <Ionicons name="checkmark-circle" size={22} color={colors.correct} />
                    )}
                    {showFeedback && selectedAnswer === option && option !== currentQ.answer && option !== currentQ.correct_answer && (
                      <Ionicons name="close-circle" size={22} color={colors.incorrect} />
                    )}
                  </View>
                </TouchableOpacity>
              ))}
            </View>
          </Animated.View>
        </ScrollView>
      </View>
    );
  };

  const renderCelebration = () => {
    const finalScore = score;
    const total = questions.length || QUIZ_QUESTION_COUNT;

    return (
      <View style={styles.phaseContainer}>
        <View style={styles.celebrationContent}>
          {/* Warm sun icon — always shown */}
          <View style={styles.celebrationIcon}>
            <Ionicons
              name="sunny"
              size={isSmallDevice ? 56 : 72}
              color={colors.accent}
            />
          </View>

          <DSText
            variant="display"
            color={iqesColors.ivory}
            align="center"
            style={styles.celebrationTitle}
          >
            {t('onboarding.celebrationTitle', language) || 'Amazing!'}
          </DSText>

          <DSText
            variant="title"
            color={iqesColors.goldBright}
            align="center"
            style={styles.celebrationSubtitle}
          >
            {(t('onboarding.celebrationSubtitle', language) || 'You got {score} out of {total} right!')
              .replace('{score}', String(finalScore))
              .replace('{total}', String(total))}
          </DSText>

          <DSText
            variant="body"
            color={iqesColors.mutedWarm}
            align="center"
            style={styles.celebrationMessage}
          >
            {t('onboarding.celebrationMessage', language) ||
              'You already know more than you think. Imagine how much you\'ll discover with a few minutes each day.'}
          </DSText>

          {/* Soft invitation */}
          <View style={styles.inviteContainer}>
            <Ionicons name="sunny" size={20} color={colors.accent} style={{ marginRight: spacing.sm }} />
            <DSText
              variant="label"
              color={colors.accent}
              style={{ fontStyle: 'italic', flex: 1 }}
            >
              {t('onboarding.celebrationInvite', language) ||
                'Come back tomorrow and we\'ll go deeper.'}
            </DSText>
          </View>
        </View>

        <View style={styles.welcomeActions}>
          <TouchableOpacity testID="onboarding-celebration-cta" style={styles.primaryButton} onPress={onComplete} activeOpacity={0.8}>
            <LinearGradient
              colors={[...colors.gradientCorrect] as [string, string]}
              style={styles.primaryButtonGradient}
            >
              <DSText variant="title" color={colors.white}>
                {t('onboarding.celebrationCTA', language) || 'Start Exploring'}
              </DSText>
              <Ionicons name="arrow-forward" size={20} color={colors.white} style={{ marginLeft: spacing.sm }} />
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <ImageBackground source={kingdomScene} style={styles.sceneBackground} imageStyle={styles.sceneImage}>
        <LinearGradient
          colors={['rgba(3, 5, 16, 0.24)', 'rgba(3, 5, 16, 0.82)', '#12081F']}
          locations={[0, 0.48, 1]}
          style={StyleSheet.absoluteFillObject}
        />
      </ImageBackground>

      {/* Language Selector */}
      <OnboardingLanguageSelector />

      {/* Skip button - always available */}
      <View style={styles.header}>
        <TouchableOpacity onPress={handleSkip} style={styles.skipButton}>
          <DSText variant="body" color={colors.onSurfaceVariant} style={{ fontWeight: '500' }}>
            {t('onboarding.skip', language) || 'Skip'}
          </DSText>
        </TouchableOpacity>
      </View>

      {/* Phase content */}
      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {phase === 'welcome' && renderWelcome()}
        {phase === 'quiz' && renderQuiz()}
        {phase === 'celebration' && renderCelebration()}
      </Animated.View>
    </SafeAreaView>
  );
};

const createStyles = (colors: OnboardingThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: iqesColors.canvas,
  },
  sceneBackground: {
    ...StyleSheet.absoluteFillObject,
  },
  sceneImage: {
    opacity: 0.78,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: spacing.xl,
    paddingTop: isSmallDevice ? 92 : spacing['4xl'],
  },
  skipButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: 'rgba(8, 10, 24, 0.72)',
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.24)',
  },
  skipText: {
    fontSize: 16,
    color: colors.onSurfaceVariant,
    fontWeight: '500',
  },
  content: {
    flex: 1,
  },
  phaseContainer: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: spacing['2xl'],
    paddingBottom: isSmallDevice ? spacing.xl : spacing['4xl'],
  },

  // Welcome
  welcomeContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  welcomeCard: {
    width: '100%',
    borderRadius: 28,
    borderWidth: 1.5,
    borderColor: 'rgba(246, 190, 58, 0.46)',
    backgroundColor: 'rgba(8, 10, 24, 0.82)',
    paddingHorizontal: isSmallDevice ? spacing.xl : spacing['2xl'],
    paddingTop: isSmallDevice ? spacing.xl : spacing['3xl'],
    paddingBottom: isSmallDevice ? spacing.xl : spacing['2xl'],
    alignItems: 'center',
    ...iqesGlow.device,
  },
  welcomeMark: {
    width: isSmallDevice ? 102 : 116,
    height: isSmallDevice ? 102 : 116,
    borderRadius: 999,
    borderWidth: 2,
    borderColor: 'rgba(255, 213, 74, 0.74)',
    backgroundColor: 'rgba(246, 190, 58, 0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
    shadowColor: iqesColors.gold,
    shadowOpacity: 0.45,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 0 },
  },
  welcomeMarkText: {
    fontSize: isSmallDevice ? 27 : 31,
    fontWeight: '900',
    letterSpacing: 1,
  },
  welcomeMarkSubtext: {
    fontSize: 9,
    fontWeight: '800',
    letterSpacing: 1.2,
  },
  eyebrow: {
    letterSpacing: 2,
    fontWeight: '800',
    marginBottom: spacing.md,
  },
  welcomeTitle: {
    fontSize: isSmallDevice ? 34 : 42,
    fontWeight: '900',
    marginBottom: isSmallDevice ? spacing.sm : spacing.md,
    lineHeight: isSmallDevice ? 40 : 50,
    textShadowColor: 'rgba(246, 190, 58, 0.28)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 12,
  },
  welcomeSubtitle: {
    fontSize: isSmallDevice ? 16 : 18,
    fontWeight: '700',
    marginBottom: spacing.md,
  },
  welcomeDescription: {
    fontSize: isSmallDevice ? 15 : 17,
    lineHeight: isSmallDevice ? 22 : 26,
    maxWidth: 340,
  },
  journeyStats: {
    width: '100%',
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xl,
  },
  statPill: {
    flex: 1,
    minHeight: 66,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.32)',
    backgroundColor: 'rgba(18, 8, 31, 0.72)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.xs,
  },
  statLabel: {
    fontWeight: '900',
    letterSpacing: 0.8,
  },
  welcomeActions: {
    paddingTop: spacing.lg,
  },
  primaryButton: {
    borderRadius: 999,
    overflow: 'hidden',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.42,
    shadowRadius: 18,
    elevation: 8,
    borderWidth: 1,
    borderColor: 'rgba(255, 232, 163, 0.42)',
  },
  primaryButtonGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: isSmallDevice ? spacing.lg : 18,
    paddingHorizontal: spacing['3xl'],
  },

  // Quiz
  quizProgressContainer: {
    alignItems: 'center',
    paddingTop: isSmallDevice ? 4 : spacing.md,
    marginBottom: isSmallDevice ? spacing.sm : spacing.lg,
  },
  quizHeaderText: {
    letterSpacing: 1.5,
    marginBottom: spacing.sm,
    fontWeight: '900',
    textShadowColor: 'rgba(246, 190, 58, 0.22)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 8,
  },
  progressBar: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  progressDot: {
    width: isSmallDevice ? 36 : 44,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.surfaceContainerHighest,
  },
  progressDotCompleted: {
    backgroundColor: colors.correct,
  },
  progressDotActive: {
    backgroundColor: colors.primary,
  },
  quizQuestionCount: {
    fontWeight: '700',
  },
  questionCard: {
    justifyContent: 'flex-start',
    width: '100%',
    borderRadius: 28,
    borderWidth: 1.25,
    borderColor: 'rgba(246, 190, 58, 0.38)',
    backgroundColor: 'rgba(8, 10, 24, 0.88)',
    paddingHorizontal: isSmallDevice ? spacing.lg : spacing.xl,
    paddingVertical: isSmallDevice ? spacing.xl : spacing['2xl'],
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.18,
    shadowRadius: 20,
    elevation: 8,
  },
  quizScrollView: {
    flex: 1,
  },
  quizScrollContent: {
    flexGrow: 1,
    justifyContent: 'flex-start',
    paddingTop: isSmallDevice ? spacing.lg : spacing.xl,
    paddingBottom: isSmallDevice ? spacing.xl : spacing['2xl'],
  },
  questionText: {
    fontSize: isSmallDevice ? 22 : 25,
    fontWeight: '900',
    marginBottom: isSmallDevice ? spacing.lg : spacing.xl,
    lineHeight: isSmallDevice ? 29 : 34,
    textShadowColor: 'rgba(246, 190, 58, 0.2)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 10,
  },
  optionsContainer: {
    gap: isSmallDevice ? spacing.sm : spacing.md,
  },
  optionButton: {
    borderRadius: 18,
    borderWidth: 1.5,
    overflow: 'hidden',
  },
  optionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: isSmallDevice ? 68 : 76,
    paddingVertical: isSmallDevice ? spacing.sm : spacing.md,
    paddingHorizontal: isSmallDevice ? spacing.md : spacing.lg,
  },
  optionDefault: {
    backgroundColor: 'rgba(13, 16, 39, 0.94)',
    borderColor: 'rgba(246, 190, 58, 0.44)',
  },
  optionSelected: {
    backgroundColor: 'rgba(197, 133, 16, 0.68)',
    borderColor: iqesColors.goldBright,
  },
  optionCorrect: {
    backgroundColor: 'rgba(12, 96, 39, 0.84)',
    borderColor: '#9DF871',
  },
  optionWrong: {
    backgroundColor: 'rgba(92, 20, 20, 0.84)',
    borderColor: '#F87171',
  },
  optionBadge: {
    width: isSmallDevice ? 38 : 42,
    height: isSmallDevice ? 38 : 42,
    borderRadius: 999,
    backgroundColor: iqesColors.ivory,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing.md,
  },
  optionBadgeCorrect: {
    backgroundColor: `${colors.correct}33`, // 0.2 opacity
  },
  optionBadgeWrong: {
    backgroundColor: `${colors.incorrect}33`, // 0.2 opacity
  },
  optionText: {
    flex: 1,
    fontSize: isSmallDevice ? 18 : 19,
    lineHeight: isSmallDevice ? 23 : 25,
  },
  optionBadgeText: {
    fontWeight: '900',
    fontSize: isSmallDevice ? 16 : 17,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
  },
  skipAheadButton: {
    marginTop: spacing.lg,
    paddingHorizontal: spacing['2xl'],
    paddingVertical: spacing.md,
    borderRadius: radius.md,
    backgroundColor: colors.surfaceContainerHigh,
  },

  // Celebration
  celebrationContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  celebrationIcon: {
    width: isSmallDevice ? 96 : 120,
    height: isSmallDevice ? 96 : 120,
    borderRadius: isSmallDevice ? 48 : 60,
    backgroundColor: 'rgba(246, 190, 58, 0.14)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: isSmallDevice ? spacing.xl : 28,
  },
  celebrationTitle: {
    fontSize: isSmallDevice ? 32 : 40,
    fontWeight: '800',
    marginBottom: spacing.sm,
  },
  celebrationSubtitle: {
    fontSize: isSmallDevice ? 18 : 22,
    fontWeight: '600',
    marginBottom: isSmallDevice ? spacing.lg : spacing['2xl'],
  },
  celebrationMessage: {
    fontSize: isSmallDevice ? 15 : 17,
    lineHeight: isSmallDevice ? 22 : 26,
    maxWidth: 320,
    marginBottom: isSmallDevice ? spacing.xl : 28,
  },
  inviteContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(246, 190, 58, 0.08)',
    paddingHorizontal: spacing.xl,
    paddingVertical: spacing.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.22)',
  },
});

export default OnboardingFlow;
